/**
 * <tf-button> — Premium button con variants, sizes, loading state, icons.
 *
 * Uso HTML:
 *   <tf-button variant="primary">Salva</tf-button>
 *   <tf-button variant="danger" loading="true">Eliminando...</tf-button>
 *   <tf-button variant="ghost" size="sm" icon-left="plus">Aggiungi</tf-button>
 *   <tf-button variant="secondary" disabled>Disabled</tf-button>
 *
 * Attributes:
 *   - variant: 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline' (default 'primary')
 *   - size: 'sm' | 'md' | 'lg' (default 'md')
 *   - type: 'button' | 'submit' | 'reset' (default 'button')
 *   - loading: 'true' (presence) — mostra spinner + disable
 *   - disabled: presence
 *   - icon-left: name SVG da preset
 *   - icon-right: name SVG da preset
 *   - href: se presente, render come <a>
 *
 * Events:
 *   - tf-button-click: bubbles (replicated da nativo, con check loading/disabled)
 *
 * @since v9.16 (Sprint 9)
 */

const VARIANTS = {
  primary: 'bg-amber-500 hover:bg-amber-600 text-white shadow-sm',
  secondary: 'bg-slate-100 hover:bg-slate-200 text-slate-900',
  danger: 'bg-red-500 hover:bg-red-600 text-white shadow-sm',
  ghost: 'bg-transparent hover:bg-slate-100 text-slate-700',
  outline: 'bg-white border border-slate-300 hover:bg-slate-50 text-slate-900',
};

const SIZES = {
  sm: 'h-9 px-3 text-sm gap-1.5',
  md: 'h-11 px-5 text-sm gap-2',
  lg: 'h-12 px-6 text-base gap-2.5',
};

const ICON_SIZE = {
  sm: 'size-4',
  md: 'size-4',
  lg: 'size-5',
};

const ICONS: Record<string, string> = {
  plus: '<path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>',
  check: '<path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75 10.5 18.75 19.5 5.25"/>',
  arrow: '<path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"/>',
  download: '<path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"/>',
  trash: '<path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>',
  edit: '<path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"/>',
};

export class TfButtonElement extends HTMLElement {
  static get observedAttributes(): string[] {
    return ['variant', 'size', 'type', 'loading', 'disabled', 'icon-left', 'icon-right', 'href'];
  }

  connectedCallback(): void {
    if (!this._capturedContent) {
      this._capturedContent = this.textContent?.trim() || '';
    }
    this._render();
  }

  attributeChangedCallback(): void {
    if (this.isConnected) this._render();
  }

  private _render(): void {
    const variant = (this.getAttribute('variant') as keyof typeof VARIANTS) || 'primary';
    const size = (this.getAttribute('size') as keyof typeof SIZES) || 'md';
    const type = this.getAttribute('type') || 'button';
    const loading = this.getAttribute('loading') === 'true';
    const disabled = this.hasAttribute('disabled') || loading;
    const iconLeft = this.getAttribute('icon-left');
    const iconRight = this.getAttribute('icon-right');
    const href = this.getAttribute('href');

    const variantCls = VARIANTS[variant] || VARIANTS.primary;
    const sizeCls = SIZES[size] || SIZES.md;
    const iconSize = ICON_SIZE[size] || ICON_SIZE.md;

    const cls = `tf-btn inline-flex items-center justify-center rounded-xl font-semibold transition-all
                 ${variantCls} ${sizeCls}
                 ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
                 ${loading ? 'pointer-events-none' : ''}`;

    const iconLeftSvg = iconLeft && ICONS[iconLeft]
      ? `<svg class="${iconSize}" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">${ICONS[iconLeft]}</svg>`
      : '';
    const iconRightSvg = iconRight && ICONS[iconRight]
      ? `<svg class="${iconSize}" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">${ICONS[iconRight]}</svg>`
      : '';

    const spinner = loading
      ? `<svg class="${iconSize} animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" opacity="0.25"/><path fill="currentColor" d="M12 2a10 10 0 0 1 10 10h-3a7 7 0 0 0-7-7V2z"/></svg>`
      : '';

    // Original content (children) come slot
    const slotContent = this._capturedContent;

    if (href && !disabled) {
      this.innerHTML = `
        <a href="${this._esc(href)}" class="${cls}" data-tf-btn>
          ${spinner || iconLeftSvg}
          <span class="tf-btn-label">${slotContent}</span>
          ${iconRightSvg}
        </a>
      `;
    } else {
      this.innerHTML = `
        <button type="${this._esc(type)}" class="${cls}" data-tf-btn ${disabled ? 'disabled' : ''}>
          ${spinner || iconLeftSvg}
          <span class="tf-btn-label">${slotContent}</span>
          ${iconRightSvg}
        </button>
      `;
    }

    // Click forward
    this.querySelector('[data-tf-btn]')?.addEventListener('click', (e) => {
      if (disabled) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      this.dispatchEvent(new CustomEvent('tf-button-click', { bubbles: true }));
    });
  }

  private _capturedContent = '';

  private _esc(s: string): string {
    return s.replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] || c
    );
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('tf-button')) {
  customElements.define('tf-button', TfButtonElement);
}
